import * as React from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchData } from '../utils/fetchData'
import MessageBox from '../components/MessageBox'
import Button from '../components/Button'
import Page from '../components/layout/Page'
import Content from '../components/layout/Content'
import Arrow from '../icons/Arrow'
import ProtectedRoute from '../helpers/ProtectedRoute'
import Modal from '../components/Modal'
import ImageCrop from '../components/ImageCrop'
import { Image } from '../types/Image'
import { Media } from '../types/User'
import styles from '../styles/ImageInfo.module.css'
import OptimizedImage from '../components/OptimizedImage'

function ImageInfo () {
  const STORAGE_KEY = 'media'
  const localStorageMedia = localStorage.getItem(STORAGE_KEY)
  let media: Media | null = null
  if (localStorageMedia) {
    try {
      media = JSON.parse(localStorageMedia)
    } catch (error) {
      console.log(error)
    }
  }
  const token = media?.token ?? null
  const { storeId, channelType, id } = useParams()
  const [image, setImage] = React.useState<Image>()
  const [croppedImageUrl, setCroppedImageUrl] = React.useState('')
  const [urlCopied, setUrlCopied] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [imageFormatModalOpen, setImageFormatModalOpen] = React.useState(false)
  const [imageUrlModalOpen, setImageUrlModalOpen] = React.useState(false)
  const [croppedImageAspectRatio, setCroppedImageAspectRatio] = React.useState<number>(1)

  function onOpenUrlModal () {
    setImageUrlModalOpen(true)
    setImageFormatModalOpen(false)
  }

  React.useEffect(() => {
    async function getImage () {
      setLoading(true)
      try {
        const res = await fetchData(`/api/cloudinary/images/${id}`, {
          method: "GET",
        })

        if (res) {
          const { data, status } = res
          if (status === 200 && data && data?.asset_id) {
            setImage(data)
          }
        }

      } catch (error) {
        console.log(error)
      }
      setLoading(false)
    }
    if (token && id) {
      getImage()
    }
  }, [token, id])

  async function copyUrl () {
    await navigator.clipboard.writeText(croppedImageUrl)
    setUrlCopied(true)
  }

  React.useEffect(() => {
    const timer = setTimeout(() => setUrlCopied(false), 5000)
    return () => { clearTimeout(timer) }
  }, [urlCopied])

  return (
    <ProtectedRoute user={token}>
      <Page>
        <div className={styles.imageDetail}>
          <Content>
            <Link
              className={styles.backButton}
              to={`/${storeId}/${channelType}`}
            >
              <Arrow className={styles.arrowIcon} />
              <span>Till bildgalleriet</span>
            </Link>
            <h1 className={styles.heading}>Sparad</h1>
            {loading ? (
              <div>Laddar…</div>
            ) : image ? (
              <div className={styles.surface}>
                <div className={styles.cover}>
                  <OptimizedImage
                    className={styles.image}
                    src={image?.secure_url}
                    sizes='(min-width: 66.5em) 33vw, 50vw'
                    aspectRatio={image?.width / image?.height}
                  />
                </div>
                <div className={styles.info}>
                  <div>
                    <p className={styles.textGroup}>
                      <span className={styles.boldText}>Bildens namn: </span>
                      <span className={styles.text}>{image?.context?.custom?.name ? `"${image?.context?.custom?.name}"` : 'Bildnamn saknas'}</span>
                    </p>
                    {image.tags && image.tags.length > 1 && (
                      <div className={styles.textGroup}>
                        <h3 className={styles.boldText}>Bildens taggar: </h3>
                        <ul className={styles.tags}>
                          {image.tags
                            .filter((t: string) => t !== channelType)
                            .map((tag: string, index: number) => (
                              <li className={styles.tag} key={index}>
                                {tag[0].toUpperCase() + tag.slice(1).toLowerCase()}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className={styles.urlContainer}>
                    <h2 className={styles.urlHeading}>Välj det bildformat bilden ska visas i och hämta URL</h2>
                    <Button size='medium' onClick={() => setImageFormatModalOpen(true)}>
                      <>
                        <Arrow className={styles.arrowIcon} direction='right' />
                        Välj bildformat och hämta url
                      </>
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div>Kunde inte hämta bild</div>
            )}
            <Modal
              onCancel={() => setImageFormatModalOpen(false)}
              active={imageFormatModalOpen}
              maxWidth="78rem"
              title="Välj det bildformat bilden ska visas i"
              height="100%"
            >
              <ImageCrop
                imgSrc={image?.secure_url}
                imgHeight={image?.height}
                imgWidth={image?.width}
                imageName={image?.context?.custom?.name ? image.context.custom.name : ''}
                imageUrl={image?.secure_url}
                onOpenUrlModal={onOpenUrlModal}
                setCroppedImageUrl={setCroppedImageUrl}
                setCroppedImageAspectRatio={setCroppedImageAspectRatio}
              />
            </Modal>
            <Modal
              onCancel={() => setImageUrlModalOpen(false)}
              active={imageUrlModalOpen}
              maxWidth="78rem"
              title="Kopiera URL:en för visning av valt bildformat"
            >
              {croppedImageUrl &&
                <div className={styles.croppedImageContainer}>
                  <OptimizedImage
                    className={styles.croppedImage}
                    src={croppedImageUrl}
                    sizes='(min-width: 66.5em) 33vw, 50vw'
                    aspectRatio={croppedImageAspectRatio}
                  />
                  <div className={styles.urlContainer}>
                    <p className={styles.boldText}>Bildens URL</p>
                    <div className={styles.urlTextBox}>
                      <input
                        id="imageUrl"
                        className={styles.urlInput}
                        type="text"
                        value={croppedImageUrl}
                        readOnly
                      />
                      <Button className={styles.urlButton} size='medium' onClick={copyUrl}>Kopiera URL</Button>
                    </div>
                  </div>
                  {urlCopied && <span className={styles.messageBox}><MessageBox type='noIcon' withTitle={false} message='URL kopierad' /></span>}
                </div>
              }
            </Modal>
          </Content>
        </div>
      </Page>
    </ProtectedRoute>
  )
}

export default ImageInfo